//frontend/src/components/Dossier/Preferences/WorkVisaPreferences.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaBriefcase, FaMapMarkerAlt, FaDollarSign, FaCalendar, FaEye } from 'react-icons/fa';
import api from '../../../services/api';
import { toast } from 'react-toastify';

const WorkVisaPreferences = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [viewOnly, setViewOnly] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    
    const [formData, setFormData] = useState({
        preferred_city: '',
        preferred_sector: '',
        job_title_sought: '',
        minimum_salary: '',
        years_experience: '',
        key_skills: '',
        certifications: '',
        available_from: '',
        willing_to_relocate: true,
        additional_notes: '',
    });

    const cities = [
        { value: 'toronto', label: 'Toronto, Ontario' },
        { value: 'montreal', label: 'Montréal, Québec' },
        { value: 'vancouver', label: 'Vancouver, Colombie-Britannique' },
        { value: 'calgary', label: 'Calgary, Alberta' },
        { value: 'ottawa', label: 'Ottawa, Ontario' },
        { value: 'edmonton', label: 'Edmonton, Alberta' },
        { value: 'winnipeg', label: 'Winnipeg, Manitoba' },
        { value: 'quebec', label: 'Québec, Québec' },
        { value: 'hamilton', label: 'Hamilton, Ontario' },
        { value: 'other', label: 'Autre ville' },
    ];

    const sectors = [
        { value: 'tech', label: 'Technologies de l\'information' },
        { value: 'health', label: 'Santé' },
        { value: 'education', label: 'Éducation' },
        { value: 'finance', label: 'Finance' },
        { value: 'construction', label: 'Construction' },
        { value: 'hospitality', label: 'Hôtellerie/Restauration' },
        { value: 'retail', label: 'Commerce de détail' },
        { value: 'manufacturing', label: 'Industrie manufacturière' },
        { value: 'agriculture', label: 'Agriculture' },
        { value: 'other', label: 'Autre secteur' },
    ];

    const loadExistingPreferences = useCallback(async () => {
        if (!id || id === 'undefined') {
            console.error('Invalid dossier ID:', id);
            toast.error('ID du dossier invalide');
            navigate('/mes-dossiers');
            return;
        }

        try {
            // Charger le dossier pour vérifier le statut
            const dossierResponse = await api.get(`/dossiers/${id}/`);
            const isDraft = dossierResponse.data.status === 'draft';
            setViewOnly(!isDraft);

            // Charger les préférences existantes
            try {
                const preferencesResponse = await api.get(`/dossiers/${id}/work-preferences/`);
                setFormData(preferencesResponse.data.preferences);
                setIsEditing(true);
            } catch (error) {
                // 404 signifie qu'aucune préférence n'existe encore
                if (error.response?.status === 404) {
                    // C'est normal, on continue en mode création
                    setIsEditing(false);
                    if (!isDraft) {
                        toast.info('Aucune préférence enregistrée pour ce dossier');
                        setTimeout(() => navigate(`/dossier/${id}`), 2000);
                    }
                } else {
                    // Autre erreur
                    console.error('Error loading preferences:', error);
                    toast.error('Erreur lors du chargement');
                }
            }
        } catch (error) {
            console.error('Error loading dossier:', error);
            toast.error('Erreur lors du chargement du dossier');
        }
    }, [id, navigate]);

    useEffect(() => {
        loadExistingPreferences();
    }, [loadExistingPreferences]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const method = isEditing ? 'patch' : 'post';
            await api[method](`/dossiers/${id}/work-preferences/`, formData);
            
            toast.success(isEditing ? '✅ Préférences mises à jour !' : '✅ Préférences enregistrées !');
            setTimeout(() => navigate(`/dossier/${id}`), 1000);
        } catch (error) {
            toast.error('❌ Erreur lors de l\'enregistrement');
            console.error('Error saving preferences:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <FaBriefcase className="me-3" />
                        {viewOnly ? 'Préférences - Visa de Travail' : 
                         isEditing ? 'Modifier les préférences - Visa de Travail' : 
                         'Préférences - Visa de Travail'}
                    </h2>
                    <p className="text-muted">
                        {viewOnly ? 'Consultation de vos préférences enregistrées' : 
                         'Renseignez vos préférences pour optimiser votre recherche d\'emploi au Canada'}
                    </p>
                </Col>
            </Row>

            {viewOnly && (
                <Alert variant="info" className="mb-4">
                    <FaEye className="me-2" />
                    Ce dossier a été soumis. Vous ne pouvez plus modifier vos préférences.
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                {/* Localisation */}
                <Card className="mb-4">
                    <Card.Header>
                        <FaMapMarkerAlt className="me-2" />
                        Localisation et secteur d'activité
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ville préférée *</Form.Label>
                                    <Form.Select
                                        name="preferred_city"
                                        value={formData.preferred_city}
                                        onChange={handleChange}
                                        required
                                        disabled={viewOnly}
                                    >
                                        <option value="">Sélectionner une ville</option>
                                        {cities.map(city => (
                                            <option key={city.value} value={city.value}>
                                                {city.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {!viewOnly && (
                                        <Form.Text className="text-muted">
                                            Choisissez la ville où vous souhaitez travailler
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Secteur d'activité *</Form.Label>
                                    <Form.Select
                                        name="preferred_sector"
                                        value={formData.preferred_sector}
                                        onChange={handleChange}
                                        required
                                        disabled={viewOnly}
                                    >
                                        <option value="">Sélectionner un secteur</option>
                                        {sectors.map(sector => (
                                            <option key={sector.value} value={sector.value}>
                                                {sector.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                name="willing_to_relocate"
                                checked={formData.willing_to_relocate}
                                onChange={handleChange}
                                label="Je suis ouvert(e) à d'autres villes si l'opportunité est intéressante"
                                disabled={viewOnly}
                            />
                        </Form.Group>
                    </Card.Body>
                </Card>

                {/* Poste recherché */}
                <Card className="mb-4">
                    <Card.Header>
                        <FaBriefcase className="me-2" />
                        Poste recherché
                    </Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Intitulé du poste recherché *</Form.Label>
                            <Form.Control
                                type="text"
                                name="job_title_sought"
                                value={formData.job_title_sought}
                                onChange={handleChange}
                                required
                                disabled={viewOnly}
                                placeholder="Ex: Développeur Web Full Stack, Infirmier(ère), Chef de projet..."
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Années d'expérience *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="years_experience"
                                        value={formData.years_experience}
                                        onChange={handleChange}
                                        required
                                        disabled={viewOnly}
                                        min="0"
                                        max="50"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        <FaDollarSign className="me-1" />
                                        Salaire minimum souhaité (CAD/an) *
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="minimum_salary"
                                        value={formData.minimum_salary}
                                        onChange={handleChange}
                                        required
                                        disabled={viewOnly}
                                        min="0"
                                        placeholder="Ex: 50000"
                                    />
                                    {!viewOnly && (
                                        <Form.Text className="text-muted">
                                            En dollars canadiens (CAD) par an
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Compétences */}
                <Card className="mb-4">
                    <Card.Header>
                        Compétences et qualifications
                    </Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Compétences clés *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="key_skills"
                                value={formData.key_skills}
                                onChange={handleChange}
                                required
                                disabled={viewOnly}
                                placeholder="Ex: JavaScript, React, Node.js, SQL, Gestion de projet..."
                            />
                            {!viewOnly && (
                                <Form.Text className="text-muted">
                                    Listez vos principales compétences, séparées par des virgules
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Certifications et formations</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="certifications"
                                value={formData.certifications}
                                onChange={handleChange}
                                disabled={viewOnly}
                                placeholder="Ex: PMP, AWS Certified, CISSP..."
                            />
                        </Form.Group>
                    </Card.Body>
                </Card>

                {/* Disponibilité */}
                <Card className="mb-4">
                    <Card.Header>
                        <FaCalendar className="me-2" />
                        Disponibilité
                    </Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Date de disponibilité *</Form.Label>
                            <Form.Control
                                type="date"
                                name="available_from"
                                value={formData.available_from}
                                onChange={handleChange}
                                required
                                disabled={viewOnly}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Informations complémentaires</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="additional_notes"
                                value={formData.additional_notes}
                                onChange={handleChange}
                                disabled={viewOnly}
                                placeholder="Ajoutez toute information que vous jugez utile..."
                            />
                        </Form.Group>
                    </Card.Body>
                </Card>

                {!viewOnly && (
                    <Alert variant="info" className="mb-4">
                        <i className="fas fa-info-circle me-2"></i>
                        <strong>Important :</strong> Ces informations nous aideront à mieux vous accompagner dans votre recherche d'emploi au Canada.
                    </Alert>
                )}

                <div className="d-flex justify-content-between">
                    <Button 
                        variant="secondary" 
                        onClick={() => navigate(`/dossier/${id}`)}
                    >
                        ← Retour
                    </Button>
                    
                    {!viewOnly && (
                        <Button 
                            type="submit" 
                            variant="primary" 
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Enregistrement...
                                </>
                            ) : (
                                isEditing ? 'Mettre à jour les préférences' : 'Enregistrer les préférences'
                            )}
                        </Button>
                    )}
                </div>
            </Form>
        </Container>
    );
};

export default WorkVisaPreferences;